import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Upload } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { Janela, Sobreposicao } from '@/componentes/JanelaDeAcao';
import { Seletor } from '@/componentes/Seletor';
import { formatarDecimal, formatarInteiro, interpretarNumero } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { paraBase64 } from '@/modulos/equipamentos/importacao';

import { apiDaPreditiva, useCatalogoDeFalhas, useFalha, useMaquinasDaPreditiva, useOpcoesDaProposta, useOrdensDaPrevisao } from './api';
import {
  CATEGORIAS,
  ROTULO_DA_CATEGORIA,
  ROTULO_DA_ORIGEM_DA_LEITURA,
  ROTULO_DO_SINAL,
  ROTULO_DO_TIPO_DE_MODELO,
  SITUACAO_DA_FALHA,
  UNIDADE_DO_SINAL,
  backtestEscrito,
  diaDoCampo,
  numeroDaOrdem,
  quando,
  retornoDaAnulacao,
  retornoDaBase,
  retornoDaClassificacao,
  retornoDaConclusao,
  retornoDaDecisao,
  retornoDaImportacao,
  retornoDaInvestigacao,
  retornoDaProposta,
  retornoDaSugestao,
  retornoDoBacktest,
  retornoDoCatalogo,
  retornoDoDesfecho,
  retornoDoLimiar,
  retornoDoModelo,
  valorContraLimite,
  type BaseMinima,
  type Categoria,
  type ConferenciaDaPlanilha,
  type DetalheDaFalha,
  type GrupoDeRecorrencia,
  type Investigacao,
  type ModeloPreditivo,
  type OrigemDaLeitura,
  type Previsao,
  type PropostaTecnica,
  type RespostaDaSugestao,
  type ResultadoDaClassificacao,
  type ResultadoDoBacktest,
  type Sinal,
  type TipoDeModelo,
} from './preditiva';
import { SeloDaPreditiva } from './Selo';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { opcaoDoModelo, useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * As janelas e gavetas da preditiva na mesa (p.29 a p.31): a tarefa curta no
 * modal ao centro, até seis campos; a classificação, o catálogo, o modelo e a
 * proposta, que pedem mais, na GAVETA à direita. Cada uma é desenhada só por
 * quem tem a licença da gravação dela (a página decide), e cada gravação
 * declara o seu retorno (R04).
 */

export function Recusa({ erro }: { readonly erro: unknown }) {
  const f = explicarFalha(erro);
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
      <span className="txt-corpo text-tinta">{f.erro}</span>
      <span className="txt-apoio text-tinta-secundaria">{f.comoResolver}</span>
    </div>
  );
}

function useInvalidar() {
  const cliente = useQueryClient();
  return () => {
    void cliente.invalidateQueries({ queryKey: ['preditiva'] });
  };
}

function Bloco({ titulo, children }: { readonly titulo: string; readonly children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="txt-sobretitulo text-tinta-secundaria">{titulo}</h3>
      {children}
    </section>
  );
}

function Fato({ rotulo, texto }: { readonly rotulo: string; readonly texto: string | null }) {
  return (
    <div className="flex flex-col gap-1 border-t border-borda-sutil pt-2 first:border-t-0 first:pt-0">
      <span className="txt-legenda text-tinta-secundaria">{rotulo}</span>
      <span className="txt-corpo text-pretty text-tinta">{texto && texto.trim() !== '' ? texto : 'Não registrado'}</span>
    </div>
  );
}

/* ================================================== a classificação (gaveta) */

interface Formulario {
  componenteId: string;
  modoId: string;
  categoria: Categoria | '';
  confianca: 'confirmada' | 'provavel';
  leitura: string;
  origem: OrigemDaLeitura;
  observacao: string;
}

const VAZIO: Formulario = { componenteId: '', modoId: '', categoria: '', confianca: 'confirmada', leitura: '', origem: 'informada', observacao: '' };

/**
 * A CLASSIFICAÇÃO DA CORRETIVA, numa gaveta à direita: os fatos da O.S. em
 * cima (o que se observou, o que se confirmou, por quê, o que se fez), a
 * sugestão da IA em azul de consulta, e o formulário. A O.S. não muda.
 */
export function GavetaDeClassificacao({
  ordemId,
  podeClassificar,
  podeSugerir,
  aoFechar,
}: {
  readonly ordemId: string | null;
  readonly podeClassificar: boolean;
  readonly podeSugerir: boolean;
  readonly aoFechar: () => void;
}) {
  const detalhe = useFalha(ordemId);
  const d = detalhe.data;
  return (
    <Sobreposicao
      lugar="direita"
      titulo={d ? `Classificar a falha da ${numeroDaOrdem(d.ordem.numero)}` : 'Classificar a falha'}
      descricao="O componente, o modo e a causa padronizados. A O.S. não muda: a classificação mora à parte, e a trilha guarda cada versão."
      aberta={ordemId !== null}
      aoFechar={aoFechar}
    >
      {detalhe.isError ? (
        <Recusa erro={detalhe.error} />
      ) : !d ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <span className="sr-only">Carregando a O.S.</span>
          {[0, 1, 2, 3].map((n) => (
            <span key={n} aria-hidden="true" className="esqueleto block h-4 w-full rounded-etiqueta" />
          ))}
        </div>
      ) : (
        <ConteudoDaClassificacao key={`${d.ordem.id}-${d.classificacao?.id ?? 'nova'}`} d={d} podeClassificar={podeClassificar} podeSugerir={podeSugerir} aoFechar={aoFechar} />
      )}
    </Sobreposicao>
  );
}

function ConteudoDaClassificacao({
  d,
  podeClassificar,
  podeSugerir,
  aoFechar,
}: {
  readonly d: DetalheDaFalha;
  readonly podeClassificar: boolean;
  readonly podeSugerir: boolean;
  readonly aoFechar: () => void;
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const c = d.classificacao;
  const [v, setV] = useState<Formulario>(
    c
      ? {
          componenteId: c.componenteId,
          modoId: c.modoId ?? '',
          categoria: c.categoria,
          confianca: c.confianca,
          leitura: c.leituraNaFalha === null ? '' : formatarDecimal(c.leituraNaFalha, 1),
          origem: c.origemDaLeitura ?? 'informada',
          observacao: c.observacao ?? '',
        }
      : {
          ...VAZIO,
          leitura: d.leituraSugerida ? formatarDecimal(d.leituraSugerida.valor, 1) : '',
          origem: d.leituraSugerida?.origem ?? 'informada',
        },
  );
  const [sugestao, setSugestao] = useState<RespostaDaSugestao | null>(null);
  const [tentou, setTentou] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const situacao = SITUACAO_DA_FALHA[d.ordem.situacao];
  const classificavel = d.ordem.situacao === 'a_classificar' || d.ordem.situacao === 'classificada';
  const leitura = v.leitura.trim() === '' ? null : interpretarNumero(v.leitura);
  const leituraSugeridaIntacta = d.leituraSugerida !== null && leitura === d.leituraSugerida.valor;
  const origem: OrigemDaLeitura = leituraSugeridaIntacta && d.leituraSugerida ? d.leituraSugerida.origem : 'informada';

  const sugerir = useMutation({
    mutationFn: () => apiDaPreditiva.sugerir(d.ordem.id),
    meta: { retorno: retornoDe<RespostaDaSugestao>((r) => ({ titulo: r.sabe ? 'Sugestão da IA recebida' : 'A IA se absteve', ...retornoDaSugestao(r) })), silenciarFalha: true },
    onSuccess: (r) => {
      setSugestao(r);
      if (r.sabe && r.componente) {
        setV((x) => ({ ...x, componenteId: r.componente?.id ?? x.componenteId, modoId: r.modo?.id ?? '', categoria: r.categoria ?? x.categoria }));
      }
    },
  });
  const gravar = useMutation({
    mutationFn: () => {
      const dados = {
        componenteId: v.componenteId,
        modoId: v.modoId || null,
        categoria: v.categoria as Categoria,
        confianca: v.confianca,
        leitura: leitura === null ? null : { valor: leitura, origem },
        observacao: v.observacao.trim() || null,
      };
      return c ? apiDaPreditiva.corrigir(c.id, dados) : apiDaPreditiva.classificar({ ...dados, ordemId: d.ordem.id, sugestaoId: sugestao?.sabe ? sugestao.id : null });
    },
    meta: { retorno: retornoDe<ResultadoDaClassificacao>((r) => ({ titulo: c ? 'Classificação corrigida' : 'Falha classificada', ...retornoDaClassificacao(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      aoFechar();
    },
  });
  const anular = useMutation({
    mutationFn: () => apiDaPreditiva.anular(c?.id ?? '', motivo.trim()),
    meta: { retorno: retornoDe<ResultadoDaClassificacao>((r) => ({ titulo: 'Classificação anulada', ...retornoDaAnulacao(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      aoFechar();
    },
  });

  const opcoesDeComponente = d.catalogo.componentes.map((x) => ({ valor: x.id, rotulo: `${x.codigo} · ${x.nome}${x.sistema ? ` (${x.sistema})` : ''}` }));
  const opcoesDeModo = d.catalogo.modos.map((x) => ({ valor: x.id, rotulo: `${x.codigo} · ${x.nome}` }));
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-2">
        <SeloDaPreditiva {...situacao} />
        <span className="txt-apoio text-tinta-secundaria">{`${d.ordem.equipamento}${d.ordem.modelo ? ` · ${d.ordem.modelo}` : ''} · aberta em ${quando(d.ordem.abertaEm)}`}</span>
      </div>
      <Bloco titulo="O que a O.S. diz">
        <div className="flex flex-col gap-2 rounded-controle bg-rebaixada p-3">
          <Fato rotulo="[1] Sintoma relatado" texto={d.ordem.sintoma} />
          <Fato rotulo="[2] Diagnóstico confirmado" texto={d.ordem.diagnostico} />
          <Fato rotulo="[3] Causa raiz" texto={d.ordem.causaRaiz} />
          <Fato rotulo="[4] Solução" texto={d.ordem.solucao} />
        </div>
        {!classificavel ? <p className="txt-apoio text-pretty text-tinta-secundaria">{situacao.oQueFazer}</p> : null}
      </Bloco>

      {classificavel && podeSugerir ? (
        <Bloco titulo="Sugestão da IA">
          <div className="flex flex-col gap-2 rounded-controle bg-informacao-fundo p-3">
            {sugerir.isError ? <Recusa erro={sugerir.error} /> : null}
            {sugestao ? (
              sugestao.sabe && sugestao.componente ? (
                <>
                  <p className="txt-corpo text-tinta">
                    {`${sugestao.componente.codigo} · ${sugestao.componente.nome}${sugestao.modo ? `, ${sugestao.modo.nome}` : ''}${sugestao.categoria ? `, ${ROTULO_DA_CATEGORIA[sugestao.categoria].toLowerCase()}` : ''}`}
                  </p>
                  {sugestao.explicacao ? <p className="txt-apoio text-pretty text-tinta-secundaria">{sugestao.explicacao}</p> : null}
                  <p className="txt-apoio text-tinta-secundaria">{`Cita: ${sugestao.citacoes.map((x) => x.titulo.replace('O.S. — ', '')).join(', ')}. É sugestão: confira antes de classificar.`}</p>
                </>
              ) : (
                <p className="txt-corpo text-pretty text-tinta">{`A IA se absteve: ${sugestao.falta ?? 'o texto da O.S. não basta'}.`}</p>
              )
            ) : (
              <p className="txt-apoio text-pretty text-tinta-secundaria">
                A IA lê os quatro fatos acima e o catálogo da empresa, e sugere só códigos do catálogo — ou diz o que falta. Nunca dá número.
              </p>
            )}
            <div>
              <Botao nivel="consulta" carregando={sugerir.isPending} onClick={() => { sugerir.mutate(); }}>
                <Sparkles aria-hidden="true" className="size-[var(--icone-controle)]" />
                Sugerir pela IA
              </Botao>
            </div>
          </div>
        </Bloco>
      ) : null}

      {classificavel && podeClassificar ? (
        <Bloco titulo={c ? 'A classificação' : 'Classificar'}>
          {gravar.isError ? <Recusa erro={gravar.error} /> : null}
          {d.catalogo.componentes.length === 0 ? (
            <p className="txt-apoio text-pretty text-tinta-secundaria">
              O catálogo de componentes está vazio. Quem tem o perfil Planejador de manutenção cadastra os componentes (ou importa a planilha) em Catálogo de falhas.
            </p>
          ) : null}
          <CampoSelecao rotulo="Componente" valor={v.componenteId} aoMudar={(x) => { setV({ ...v, componenteId: x }); }} opcoes={opcoesDeComponente} rotuloVazio="Escolha o componente" />
          {tentou && !v.componenteId ? <p className="txt-apoio text-erro-texto">Escolha o componente que falhou.</p> : null}
          <CampoSelecao rotulo="Modo de falha" opcional valor={v.modoId} aoMudar={(x) => { setV({ ...v, modoId: x }); }} opcoes={opcoesDeModo} rotuloVazio="Sem modo" />
          <CampoSelecao
            rotulo="Categoria da causa"
            valor={v.categoria}
            aoMudar={(x) => { setV({ ...v, categoria: x as Categoria }); }}
            opcoes={CATEGORIAS.map((x) => ({ valor: x, rotulo: ROTULO_DA_CATEGORIA[x] }))}
            rotuloVazio="Escolha a categoria"
          />
          {tentou && !v.categoria ? <p className="txt-apoio text-erro-texto">Escolha a categoria da causa; sem certeza, use Indeterminada.</p> : null}
          <div className="flex flex-col gap-2">
            <span className="txt-rotulo-campo">Confiança</span>
            <Seletor
              rotulo="Confiança"
              valor={v.confianca}
              opcoes={[{ valor: 'confirmada', rotulo: 'Confirmada' }, { valor: 'provavel', rotulo: 'Provável' }]}
              aoEscolher={(x) => { setV({ ...v, confianca: x }); }}
            />
          </div>
          <Campo
            id={`${base}-leitura`}
            rotulo={d.ordem.tipoMedidor === 'odometro' ? 'Odômetro na falha' : 'Horímetro na falha'}
            opcional
            preenchido={leituraSugeridaIntacta}
            ajuda={
              leituraSugeridaIntacta && d.leituraSugerida
                ? `Veio de ${ROTULO_DA_ORIGEM_DA_LEITURA[d.leituraSugerida.origem]}${d.leituraSugerida.lidaEm ? `, de ${quando(d.leituraSugerida.lidaEm)}` : ''}. Confira; sem a leitura, a falha não entra nos intervalos.`
                : 'Sem a leitura, a falha entra na recorrência, mas não nos intervalos entre falhas.'
            }
            erro={tentou && v.leitura.trim() !== '' && leitura === null ? 'Escreva a leitura como número, por exemplo 4.832,5.' : null}
          >
            <input id={`${base}-leitura`} inputMode="decimal" className="controle tabular-nums" value={v.leitura} placeholder="4.832,5" onChange={(e) => { setV({ ...v, leitura: e.target.value }); }} />
          </Campo>
          <CampoTexto rotulo="Observação" opcional valor={v.observacao} aoMudar={(x) => { setV({ ...v, observacao: x }); }} maxLength={400} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {c ? (
              <Botao nivel="destrutivo" onClick={() => { setAnulando(true); }}>
                Anular classificação
              </Botao>
            ) : null}
            <Botao
              nivel="primario"
              carregando={gravar.isPending}
              onClick={() => {
                setTentou(true);
                if (!v.componenteId || !v.categoria || (v.leitura.trim() !== '' && leitura === null)) return;
                gravar.mutate();
              }}
            >
              {c ? 'Salvar correção' : 'Classificar falha'}
            </Botao>
          </div>
        </Bloco>
      ) : null}

      {c && podeClassificar ? (
        <Janela
          titulo={`Anular a classificação da ${numeroDaOrdem(d.ordem.numero)}?`}
          descricao="Ela sai do histórico e da recorrência. A trilha guarda quem anulou e por quê; a O.S. não muda."
          aberta={anulando}
          largura="p"
          aoFechar={() => { setAnulando(false); }}
          acoes={
            <>
              <Botao nivel="terciario" onClick={() => { setAnulando(false); }}>
                Manter classificação
              </Botao>
              <Botao nivel="destrutivo" disabled={motivo.trim().length < 10} carregando={anular.isPending} onClick={() => { anular.mutate(); }}>
                Anular classificação
              </Botao>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            {anular.isError ? <Recusa erro={anular.error} /> : null}
            <CampoTexto rotulo="Motivo" valor={motivo} aoMudar={setMotivo} placeholder="Classificada na O.S. errada" ajuda="Pelo menos 10 caracteres. Fica na trilha com o seu nome." maxLength={400} />
          </div>
        </Janela>
      ) : null}
    </div>
  );
}

/* ================================================== o catálogo (gaveta) */

export function GavetaDoCatalogo({ aberta, podeGerir, aoFechar }: { readonly aberta: boolean; readonly podeGerir: boolean; readonly aoFechar: () => void }) {
  const catalogo = useCatalogoDeFalhas(aberta);
  const [aba, setAba] = useState<'componentes' | 'modos' | 'limites' | 'planilha'>('componentes');
  const c = catalogo.data;
  return (
    <Sobreposicao
      lugar="direita"
      titulo="Catálogo de falhas"
      descricao="Os componentes, os modos de falha e o limite de sinal do fabricante da empresa. Começa vazio: nada é inventado."
      aberta={aberta}
      aoFechar={aoFechar}
    >
      <div className="flex flex-col gap-4">
        <Seletor
          rotulo="Parte do catálogo"
          valor={aba}
          aoEscolher={setAba}
          opcoes={[
            { valor: 'componentes', rotulo: 'Componentes' },
            { valor: 'modos', rotulo: 'Modos' },
            { valor: 'limites', rotulo: 'Limites' },
            ...(podeGerir ? [{ valor: 'planilha' as const, rotulo: 'Planilha' }] : []),
          ]}
        />
        {catalogo.isError ? <Recusa erro={catalogo.error} /> : null}
        {!c ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <span className="sr-only">Carregando o catálogo</span>
            {[0, 1, 2].map((n) => (
              <span key={n} aria-hidden="true" className="esqueleto block h-4 w-full rounded-etiqueta" />
            ))}
          </div>
        ) : aba === 'componentes' ? (
          <ListaDoCatalogo
            itens={c.componentes.map((x) => ({ id: x.id, codigo: x.codigo, nome: x.nome, detalhe: [x.sistema ? `sistema ${x.sistema}` : null, x.modelo || null, `${formatarInteiro(x.falhas)} falhas`].filter(Boolean).join(' · '), ativo: x.ativo }))}
            vazio="Nenhum componente cadastrado. Cadastre o primeiro ou importe a planilha da empresa."
            podeGerir={podeGerir}
            alternar={(id, ativo) => apiDaPreditiva.editarComponente(id, { ativo })}
            formulario={podeGerir ? <NovoComponente sistemas={c.componentes.filter((x) => x.ativo)} modelos={c.modelos} /> : null}
          />
        ) : aba === 'modos' ? (
          <ListaDoCatalogo
            itens={c.modos.map((x) => ({ id: x.id, codigo: x.codigo, nome: x.nome, detalhe: `${formatarInteiro(x.falhas)} falhas`, ativo: x.ativo }))}
            vazio="Nenhum modo de falha cadastrado. Cadastre o primeiro ou importe a planilha da empresa."
            podeGerir={podeGerir}
            alternar={(id, ativo) => apiDaPreditiva.editarModo(id, { ativo })}
            formulario={podeGerir ? <NovoModo /> : null}
          />
        ) : aba === 'limites' ? (
          <ListaDoCatalogo
            itens={c.limiares.map((x) => ({
              id: x.id,
              codigo: ROTULO_DO_SINAL[x.tipo],
              nome: `${x.sentido} de ${formatarDecimal(x.limite, 1).replace(/,0$/, '')} ${UNIDADE_DO_SINAL[x.tipo]} · ${x.modelo}`,
              detalhe: `${x.documento}, p. ${formatarInteiro(x.pagina)}${x.componente ? ` · ${x.componente}` : ''}`,
              ativo: x.ativo,
            }))}
            vazio="Nenhum limite de sinal. O limite vem do manual do fabricante, com a página: sem o manual na biblioteca técnica, não há limite."
            podeGerir={podeGerir}
            alternar={(id, ativo) => apiDaPreditiva.editarLimiar(id, ativo)}
            formulario={podeGerir ? <NovoLimiar modelos={c.modelos} documentos={c.documentos} componentes={c.componentes.filter((x) => x.ativo)} /> : null}
          />
        ) : podeGerir ? (
          <PlanilhaDoCatalogo />
        ) : null}
      </div>
    </Sobreposicao>
  );
}

function ListaDoCatalogo({
  itens,
  vazio,
  podeGerir,
  alternar,
  formulario,
}: {
  readonly itens: readonly { readonly id: string; readonly codigo: string; readonly nome: string; readonly detalhe: string; readonly ativo: boolean }[];
  readonly vazio: string;
  readonly podeGerir: boolean;
  readonly alternar: (id: string, ativo: boolean) => Promise<unknown>;
  readonly formulario: ReactNode;
}) {
  const invalidar = useInvalidar();
  const mudar = useMutation({
    mutationFn: (x: { id: string; ativo: boolean; codigo: string; nome: string }) => alternar(x.id, x.ativo),
    meta: {
      retorno: retornoDe<unknown, { id: string; ativo: boolean; codigo: string; nome: string }>((_r, x) => ({
        titulo: x.ativo ? 'Item reativado' : 'Item desativado',
        alvo: { codigo: x.codigo, nome: x.nome },
        efeito: x.ativo ? 'Volta a ser oferecido na classificação' : 'Sai da classificação nova; o histórico que aponta para ele fica',
      })),
    },
    onSuccess: invalidar,
  });
  return (
    <div className="flex flex-col gap-4">
      {itens.length === 0 ? (
        <p className="txt-apoio text-pretty text-tinta-secundaria">{vazio}</p>
      ) : (
        <ul className="flex flex-col">
          {itens.map((x) => (
            <li key={x.id} className="flex items-center gap-3 border-t border-borda-sutil py-2 first:border-t-0">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className={`txt-corpo ${x.ativo ? 'text-tinta' : 'text-tinta-secundaria'}`}>
                  <span className="codigo">{x.codigo}</span>
                  {` · ${x.nome}`}
                </span>
                <span className="txt-apoio text-tinta-secundaria">{x.ativo ? x.detalhe : `Desativado · ${x.detalhe}`}</span>
              </span>
              {podeGerir ? (
                <Botao nivel="fantasma" tamanho="p" disabled={mudar.isPending} onClick={() => { mudar.mutate({ id: x.id, ativo: !x.ativo, codigo: x.codigo, nome: x.nome }); }}>
                  {x.ativo ? 'Desativar' : 'Reativar'}
                </Botao>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {formulario}
    </div>
  );
}

function NovoComponente({ sistemas, modelos }: { readonly sistemas: readonly { id: string; codigo: string; nome: string }[]; readonly modelos: readonly { id: string; nome: string }[] }) {
  const invalidar = useInvalidar();
  const [v, setV] = useState({ codigo: '', nome: '', sistemaId: '', modeloId: '' });
  const salvar = useMutation({
    mutationFn: () => apiDaPreditiva.cadastrarComponente({ codigo: v.codigo, nome: v.nome, sistemaId: v.sistemaId || null, modeloId: v.modeloId || null, observacao: null }),
    meta: { retorno: retornoDe<{ codigo: string; nome: string }>((r) => ({ titulo: 'Componente cadastrado', ...retornoDoCatalogo(r, 'componente') })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      setV({ codigo: '', nome: '', sistemaId: '', modeloId: '' });
    },
  });
  return (
    <Bloco titulo="Novo componente">
      {salvar.isError ? <Recusa erro={salvar.error} /> : null}
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-3">
        <CampoTexto rotulo="Código" valor={v.codigo} aoMudar={(x) => { setV({ ...v, codigo: x }); }} placeholder="RAD" maxLength={30} />
        <CampoTexto rotulo="Nome" valor={v.nome} aoMudar={(x) => { setV({ ...v, nome: x }); }} placeholder="Radiador" maxLength={120} />
      </div>
      <CampoSelecao rotulo="Sistema" opcional valor={v.sistemaId} aoMudar={(x) => { setV({ ...v, sistemaId: x }); }} opcoes={sistemas.map((s) => ({ valor: s.id, rotulo: `${s.codigo} · ${s.nome}` }))} rotuloVazio="Sem sistema (é o próprio sistema)" />
      <CampoSelecao rotulo="Só no modelo" opcional valor={v.modeloId} aoMudar={(x) => { setV({ ...v, modeloId: x }); }} opcoes={modelos.map((m) => opcaoDoModelo(m))} rotuloVazio="Qualquer modelo" />
      <div className="flex justify-end">
        <Botao nivel="secundario" disabled={!v.codigo.trim() || !v.nome.trim()} carregando={salvar.isPending} onClick={() => { salvar.mutate(); }}>
          Cadastrar componente
        </Botao>
      </div>
    </Bloco>
  );
}

function NovoModo() {
  const invalidar = useInvalidar();
  const [v, setV] = useState({ codigo: '', nome: '' });
  const salvar = useMutation({
    mutationFn: () => apiDaPreditiva.cadastrarModo({ codigo: v.codigo, nome: v.nome, observacao: null }),
    meta: { retorno: retornoDe<{ codigo: string; nome: string }>((r) => ({ titulo: 'Modo de falha cadastrado', ...retornoDoCatalogo(r, 'modo') })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      setV({ codigo: '', nome: '' });
    },
  });
  return (
    <Bloco titulo="Novo modo de falha">
      {salvar.isError ? <Recusa erro={salvar.error} /> : null}
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-3">
        <CampoTexto rotulo="Código" valor={v.codigo} aoMudar={(x) => { setV({ ...v, codigo: x }); }} placeholder="VAZ" maxLength={30} />
        <CampoTexto rotulo="Nome" valor={v.nome} aoMudar={(x) => { setV({ ...v, nome: x }); }} placeholder="Vazamento" maxLength={120} />
      </div>
      <div className="flex justify-end">
        <Botao nivel="secundario" disabled={!v.codigo.trim() || !v.nome.trim()} carregando={salvar.isPending} onClick={() => { salvar.mutate(); }}>
          Cadastrar modo
        </Botao>
      </div>
    </Bloco>
  );
}

function NovoLimiar({
  modelos,
  documentos,
  componentes,
}: {
  readonly modelos: readonly { id: string; nome: string }[];
  readonly documentos: readonly { id: string; titulo: string; paginas: number | null }[];
  readonly componentes: readonly { id: string; codigo: string; nome: string }[];
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const [v, setV] = useState({ modeloId: '', componenteId: '', tipo: 'temperatura' as Sinal, sentido: 'acima' as 'acima' | 'abaixo', limite: '', documentoId: '', pagina: '' });
  const limite = interpretarNumero(v.limite);
  const pagina = interpretarNumero(v.pagina);
  const salvar = useMutation({
    mutationFn: () =>
      apiDaPreditiva.cadastrarLimiar({
        modeloId: v.modeloId, componenteId: v.componenteId || null, tipo: v.tipo, sentido: v.sentido, limite: limite ?? 0, documentoId: v.documentoId, pagina: pagina ?? 0, trecho: null,
      }),
    meta: { retorno: retornoDe<{ tipo: Sinal; sentido: 'acima' | 'abaixo'; limite: number; pagina: number }>((r) => ({ titulo: 'Limite do fabricante cadastrado', ...retornoDoLimiar(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      setV({ ...v, limite: '', pagina: '' });
    },
  });
  const pronto = v.modeloId && v.documentoId && limite !== null && pagina !== null && Number.isInteger(pagina) && pagina > 0;
  return (
    <Bloco titulo="Novo limite do fabricante">
      {salvar.isError ? <Recusa erro={salvar.error} /> : null}
      {documentos.length === 0 ? (
        <p className="txt-apoio text-pretty text-tinta-secundaria">Nenhum documento do fabricante na biblioteca técnica. Envie o manual em Biblioteca técnica e volte aqui.</p>
      ) : null}
      <CampoSelecao rotulo="Modelo de máquina" valor={v.modeloId} aoMudar={(x) => { setV({ ...v, modeloId: x }); }} opcoes={modelos.map((m) => opcaoDoModelo(m))} rotuloVazio="Escolha o modelo" />
      <div className="grid grid-cols-2 gap-3">
        <CampoSelecao rotulo="Sinal" valor={v.tipo} aoMudar={(x) => { setV({ ...v, tipo: x as Sinal }); }} opcoes={[{ valor: 'temperatura', rotulo: 'Temperatura' }, { valor: 'combustivel_nivel', rotulo: 'Nível de combustível' }]} />
        <CampoSelecao rotulo="Alerta" valor={v.sentido} aoMudar={(x) => { setV({ ...v, sentido: x as 'acima' | 'abaixo' }); }} opcoes={[{ valor: 'acima', rotulo: 'Acima do limite' }, { valor: 'abaixo', rotulo: 'Abaixo do limite' }]} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Campo id={`${base}-limite`} rotulo={`Limite (${UNIDADE_DO_SINAL[v.tipo]})`}>
          <input id={`${base}-limite`} inputMode="decimal" className="controle tabular-nums" value={v.limite} placeholder="105" onChange={(e) => { setV({ ...v, limite: e.target.value }); }} />
        </Campo>
        <Campo id={`${base}-pagina`} rotulo="Página do manual">
          <input id={`${base}-pagina`} inputMode="numeric" className="controle tabular-nums" value={v.pagina} placeholder="112" onChange={(e) => { setV({ ...v, pagina: e.target.value }); }} />
        </Campo>
      </div>
      <CampoSelecao rotulo="Manual do fabricante" valor={v.documentoId} aoMudar={(x) => { setV({ ...v, documentoId: x }); }} opcoes={documentos.map((d) => ({ valor: d.id, rotulo: d.titulo }))} rotuloVazio="Escolha o manual" />
      <CampoSelecao rotulo="Componente" opcional valor={v.componenteId} aoMudar={(x) => { setV({ ...v, componenteId: x }); }} opcoes={componentes.map((m) => ({ valor: m.id, rotulo: `${m.codigo} · ${m.nome}` }))} rotuloVazio="Sem componente" />
      <div className="flex justify-end">
        <Botao nivel="secundario" disabled={!pronto} carregando={salvar.isPending} onClick={() => { salvar.mutate(); }}>
          Cadastrar limite
        </Botao>
      </div>
    </Bloco>
  );
}

/** A PLANILHA: conferir antes de valer (p.43) — o botão diz quanto entra. */
function PlanilhaDoCatalogo() {
  const base = useId();
  const invalidar = useInvalidar();
  const [arquivo, setArquivo] = useState<{ nomeArquivo: string; conteudo: string } | null>(null);
  const [conferencia, setConferencia] = useState<ConferenciaDaPlanilha | null>(null);
  const conferir = useMutation({
    mutationFn: (a: { nomeArquivo: string; conteudo: string }) => apiDaPreditiva.conferirPlanilha(a),
    meta: { silencioso: true, silenciarFalha: true },
    onSuccess: setConferencia,
  });
  const importar = useMutation({
    mutationFn: () => apiDaPreditiva.importarPlanilha(arquivo ?? { nomeArquivo: '', conteudo: '' }),
    meta: { retorno: retornoDe<{ componentes: number; modos: number; deFora: number }>((r) => ({ titulo: 'Catálogo importado', ...retornoDaImportacao(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      setArquivo(null);
      setConferencia(null);
    },
  });
  return (
    <div className="flex flex-col gap-4">
      <p className="txt-apoio text-pretty text-tinta-secundaria">
        Uma linha por item, com as colunas Tipo (Componente ou Modo), Código, Nome, Sistema (o código do componente pai) e Modelo. Em .xlsx ou .csv. Nada entra antes da conferência.
      </p>
      <Campo id={`${base}-arquivo`} rotulo="Planilha">
        <input
          id={`${base}-arquivo`}
          type="file"
          accept=".xlsx,.csv"
          className="controle"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            void f.arrayBuffer().then((b) => {
              const a = { nomeArquivo: f.name, conteudo: paraBase64(b) };
              setArquivo(a);
              conferir.mutate(a);
            });
          }}
        />
      </Campo>
      {conferir.isError ? <Recusa erro={conferir.error} /> : null}
      {importar.isError ? <Recusa erro={importar.error} /> : null}
      {conferencia ? (
        <>
          <p className="txt-corpo text-tinta">
            {`${formatarInteiro(conferencia.entram)} entram · ${formatarInteiro(conferencia.jaCadastrados)} já cadastrados · ${formatarInteiro(conferencia.comErro)} com erro`}
          </p>
          <ul className="flex max-h-72 flex-col overflow-auto">
            {conferencia.linhas.map((l) => (
              <li key={l.numeroLinha} className="flex flex-col border-t border-borda-sutil py-2 first:border-t-0">
                <span className="txt-corpo text-tinta">{`Linha ${formatarInteiro(l.numeroLinha)} · ${l.codigo ?? 'sem código'} · ${l.nome ?? 'sem nome'}`}</span>
                <span className={`txt-apoio ${l.situacao === 'erro' ? 'text-erro-texto' : 'text-tinta-secundaria'}`}>
                  {l.situacao === 'entra' ? 'Entra' : l.situacao === 'ja_cadastrado' ? 'Já cadastrado: fica como está' : l.erros.join(' ')}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <Botao nivel="primario" disabled={conferencia.entram === 0} carregando={importar.isPending} onClick={() => { importar.mutate(); }}>
              <Upload aria-hidden="true" className="size-[var(--icone-controle)]" />
              {`Importar ${formatarInteiro(conferencia.entram)} ${conferencia.entram === 1 ? 'item' : 'itens'}`}
            </Botao>
          </div>
        </>
      ) : null}
    </div>
  );
}

/* ================================================== a base mínima */

export function JanelaDaBaseMinima({ base, aberta, podeMudar, aoFechar }: { readonly base: BaseMinima | null; readonly aberta: boolean; readonly podeMudar: boolean; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const id = useId();
  const [v, setV] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  const campos: readonly { chave: keyof Omit<BaseMinima, 'definidaPelaEmpresa'>; rotulo: string; unidade: string }[] = [
    { chave: 'falhasMinimasRecorrencia', rotulo: 'Recorrência: falhas do mesmo componente e modo', unidade: 'falhas' },
    { chave: 'maquinasMinimasRecorrencia', rotulo: 'Recorrência: máquinas do mesmo modelo', unidade: 'máquinas' },
    { chave: 'exposicaoMinimaH', rotulo: 'Taxa: exposição da família', unidade: 'h' },
    { chave: 'intervalosMinimosPrevisao', rotulo: 'Previsão por horas: intervalos entre falhas', unidade: 'intervalos' },
    { chave: 'mesesMinimosHistorico', rotulo: 'Previsão por horas: histórico classificado', unidade: 'meses' },
    { chave: 'coberturaMinimaSinalPct', rotulo: 'Previsão por sinal: cobertura da janela', unidade: '%' },
    { chave: 'previsoesMinimasAdocao', rotulo: 'Adoção: previsões com desfecho', unidade: 'previsões' },
    { chave: 'metaPrecisaoPct', rotulo: 'Adoção: meta de precisão', unidade: '%' },
  ];
  const valor = (k: (typeof campos)[number]['chave']) => v[k] ?? (base ? formatarDecimal(base[k], 1).replace(/,0$/, '') : '');
  const salvar = useMutation({
    mutationFn: () => {
      const d = Object.fromEntries(campos.map((c) => [c.chave, interpretarNumero(valor(c.chave)) ?? 0])) as Omit<BaseMinima, 'definidaPelaEmpresa'>;
      return apiDaPreditiva.mudarBase({ ...d, motivo: motivo.trim() });
    },
    meta: { retorno: retornoDe<BaseMinima>((r) => ({ titulo: 'Base mínima alterada', ...retornoDaBase(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      aoFechar();
    },
  });
  return (
    <Sobreposicao
      lugar="direita"
      titulo="Base mínima da empresa"
      descricao={
        base?.definidaPelaEmpresa
          ? 'Abaixo destes números a tela se abstém e diz o que falta. A empresa escreveu a dela.'
          : 'Abaixo destes números a tela se abstém e diz o que falta. São os padrões recomendados: o guia não dá número, e o dono pode mudar.'
      }
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        podeMudar ? (
          <>
            <Botao nivel="terciario" onClick={aoFechar}>
              Cancelar
            </Botao>
            <Botao nivel="primario" disabled={motivo.trim().length < 10} carregando={salvar.isPending} onClick={() => { salvar.mutate(); }}>
              Salvar base mínima
            </Botao>
          </>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {salvar.isError ? <Recusa erro={salvar.error} /> : null}
        {campos.map((c) =>
          podeMudar ? (
            <Campo key={c.chave} id={`${id}-${c.chave}`} rotulo={`${c.rotulo} (${c.unidade})`}>
              <input id={`${id}-${c.chave}`} inputMode="decimal" className="controle tabular-nums" value={valor(c.chave)} onChange={(e) => { setV({ ...v, [c.chave]: e.target.value }); }} />
            </Campo>
          ) : (
            <div key={c.chave} className="flex items-baseline justify-between gap-3 border-t border-borda-sutil py-2 first:border-t-0">
              <span className="txt-apoio text-tinta-secundaria">{c.rotulo}</span>
              <span className="txt-corpo tabular-nums text-tinta">{`${valor(c.chave)} ${c.unidade}`}</span>
            </div>
          ),
        )}
        {podeMudar ? (
          <CampoTexto rotulo="Motivo" valor={motivo} aoMudar={setMotivo} placeholder="Meta do piloto decidida pelo dono" ajuda="Pelo menos 10 caracteres. Fica na trilha com o seu nome." maxLength={400} />
        ) : (
          <p className="txt-apoio text-tinta-secundaria">Quem muda a base mínima é o perfil Administrador.</p>
        )}
      </div>
    </Sobreposicao>
  );
}

/* ================================================== a investigação */

export function JanelaDeInvestigacao({ grupo, aberta, aoFechar }: { readonly grupo: GrupoDeRecorrencia | null; readonly aberta: boolean; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [titulo, setTitulo] = useState('');
  const [hipotese, setHipotese] = useState('');
  const abrir = useMutation({
    mutationFn: () =>
      apiDaPreditiva.abrirInvestigacao({
        titulo: titulo.trim() || (grupo ? `${grupo.componente}${grupo.modo ? ` · ${grupo.modo}` : ''} no ${grupo.modelo}` : ''),
        modeloId: grupo?.modeloId ?? null,
        componenteId: grupo?.componenteId ?? null,
        modoId: grupo?.modoId ?? null,
        hipotese: hipotese.trim(),
        ordemIds: grupo?.ordemIds ?? [],
      }),
    meta: { retorno: retornoDe<{ titulo: string; evidencias: number }>((r) => ({ titulo: 'Investigação aberta', ...retornoDaInvestigacao(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      setTitulo('');
      setHipotese('');
      aoFechar();
    },
  });
  return (
    <Janela
      titulo="Abrir investigação"
      descricao={grupo ? `As ${formatarInteiro(grupo.falhas)} O.S. do grupo entram como evidência. A hipótese não é causa: a conclusão vem depois.` : 'A hipótese não é causa: a conclusão vem depois.'}
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" disabled={hipotese.trim().length < 15} carregando={abrir.isPending} onClick={() => { abrir.mutate(); }}>
            Abrir investigação
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {abrir.isError ? <Recusa erro={abrir.error} /> : null}
        <CampoTexto rotulo="Título" opcional valor={titulo} aoMudar={setTitulo} placeholder={grupo ? `${grupo.componente} no ${grupo.modelo}` : 'Radiador do 320D'} maxLength={120} />
        <CampoTexto rotulo="Hipótese" valor={hipotese} aoMudar={setHipotese} placeholder="O barro da obra seca entope a colmeia antes do intervalo" ajuda="Pelo menos 15 caracteres." maxLength={2000} />
      </div>
    </Janela>
  );
}

export function JanelaDeConclusao({ alvo, aoFechar }: { readonly alvo: Investigacao | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [situacao, setSituacao] = useState<'concluida' | 'descartada'>('concluida');
  const [conclusao, setConclusao] = useState('');
  const concluir = useMutation({
    mutationFn: () => apiDaPreditiva.concluirInvestigacao(alvo?.id ?? '', situacao, conclusao.trim()),
    meta: { retorno: retornoDe<{ titulo: string; situacao: string }>((r) => ({ titulo: r.situacao === 'concluida' ? 'Investigação concluída' : 'Investigação descartada', ...retornoDaConclusao(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      setConclusao('');
      aoFechar();
    },
  });
  return (
    <Janela
      titulo={alvo ? `Encerrar a investigação ${alvo.titulo}?` : 'Encerrar a investigação?'}
      descricao="A conclusão fica como for escrita: a investigação encerrada não muda. Se o assunto voltar, abra outra."
      aberta={alvo !== null}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Manter aberta
          </Botao>
          <Botao nivel="primario" disabled={conclusao.trim().length < 15} carregando={concluir.isPending} onClick={() => { concluir.mutate(); }}>
            {situacao === 'concluida' ? 'Concluir investigação' : 'Descartar investigação'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {concluir.isError ? <Recusa erro={concluir.error} /> : null}
        <Seletor rotulo="Como encerra" valor={situacao} aoEscolher={setSituacao} opcoes={[{ valor: 'concluida', rotulo: 'Concluída' }, { valor: 'descartada', rotulo: 'Descartada' }]} />
        <CampoTexto rotulo="Conclusão" valor={conclusao} aoMudar={setConclusao} placeholder="A limpeza a cada 250 h na obra seca resolve" ajuda="Pelo menos 15 caracteres." maxLength={4000} />
      </div>
    </Janela>
  );
}

/* ================================================== o modelo */

export function GavetaDeModelo({ aberta, aoFechar, sugeridos }: { readonly aberta: boolean; readonly aoFechar: () => void; readonly sugeridos: { readonly taxa: { percentil: number; horizonteH: number }; readonly limiar: { janelaH: number; leiturasMinimas: number; horizonteDias: number } } | null }) {
  const base = useId();
  const invalidar = useInvalidar();
  const catalogo = useCatalogoDeFalhas(aberta);
  const c = catalogo.data;
  const [v, setV] = useState({ nome: '', tipo: 'taxa_por_horas' as TipoDeModelo, modeloId: '', componenteId: '', modoId: '', percentil: '', horizonteH: '', limiarId: '', janelaH: '', leiturasMinimas: '', horizonteDias: '' });
  const n = (x: string, padrao: number | undefined) => (x.trim() === '' ? (padrao ?? null) : interpretarNumero(x));
  const parametros =
    v.tipo === 'taxa_por_horas'
      ? { percentil: (n(v.percentil, sugeridos ? sugeridos.taxa.percentil * 100 : undefined) ?? 0) / 100, horizonteH: n(v.horizonteH, sugeridos?.taxa.horizonteH) ?? 0 }
      : { limiarId: v.limiarId, janelaH: n(v.janelaH, sugeridos?.limiar.janelaH) ?? 0, leiturasMinimas: n(v.leiturasMinimas, sugeridos?.limiar.leiturasMinimas) ?? 0, horizonteDias: n(v.horizonteDias, sugeridos?.limiar.horizonteDias) ?? 0 };
  const criar = useMutation({
    mutationFn: () => apiDaPreditiva.criarModelo({ nome: v.nome.trim(), tipo: v.tipo, modeloId: v.modeloId, componenteId: v.componenteId, modoId: v.modoId || null, parametros }),
    meta: { retorno: retornoDe<{ nome: string; versao: number }>((r) => ({ titulo: 'Modelo criado', ...retornoDoModelo(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      aoFechar();
    },
  });
  const limiares = (c?.limiares ?? []).filter((l) => l.ativo && l.modeloId === v.modeloId);
  const pronto = v.nome.trim().length >= 3 && v.modeloId && v.componenteId && (v.tipo === 'taxa_por_horas' || v.limiarId);
  return (
    <Sobreposicao
      lugar="direita"
      titulo="Novo modelo preditivo"
      descricao="Nasce em rascunho. Vai a piloto só depois do backtest, que treina com o que veio antes do corte e mede o depois. O número sai de conta auditável, nunca da IA."
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" disabled={!pronto} carregando={criar.isPending} onClick={() => { criar.mutate(); }}>
            Criar modelo
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {criar.isError ? <Recusa erro={criar.error} /> : null}
        <CampoTexto rotulo="Nome" valor={v.nome} aoMudar={(x) => { setV({ ...v, nome: x }); }} placeholder="Radiador do 320D por horas" maxLength={80} />
        <div className="flex flex-col gap-2">
          <span className="txt-rotulo-campo">Tipo</span>
          <Seletor rotulo="Tipo do modelo" valor={v.tipo} aoEscolher={(x) => { setV({ ...v, tipo: x }); }} opcoes={[{ valor: 'taxa_por_horas', rotulo: 'Por horas' }, { valor: 'limiar_de_sinal', rotulo: 'Por sinal' }]} />
          <span className="txt-ajuda">{ROTULO_DO_TIPO_DE_MODELO[v.tipo]}</span>
        </div>
        <CampoSelecao rotulo="Modelo de máquina (a família)" valor={v.modeloId} aoMudar={(x) => { setV({ ...v, modeloId: x, limiarId: '' }); }} opcoes={(c?.modelos ?? []).map((m) => opcaoDoModelo(m))} rotuloVazio="Escolha o modelo" />
        <CampoSelecao rotulo="Componente" valor={v.componenteId} aoMudar={(x) => { setV({ ...v, componenteId: x }); }} opcoes={(c?.componentes ?? []).filter((x) => x.ativo).map((x) => ({ valor: x.id, rotulo: `${x.codigo} · ${x.nome}` }))} rotuloVazio="Escolha o componente" />
        <CampoSelecao rotulo="Modo de falha" opcional valor={v.modoId} aoMudar={(x) => { setV({ ...v, modoId: x }); }} opcoes={(c?.modos ?? []).filter((x) => x.ativo).map((x) => ({ valor: x.id, rotulo: `${x.codigo} · ${x.nome}` }))} rotuloVazio="Qualquer modo" />
        {v.tipo === 'taxa_por_horas' ? (
          <div className="grid grid-cols-2 gap-3">
            <Campo id={`${base}-p`} rotulo="Percentil (%)" ajuda="Do intervalo entre falhas. Recomendado: 50.">
              <input id={`${base}-p`} inputMode="decimal" className="controle tabular-nums" value={v.percentil} placeholder={sugeridos ? String(sugeridos.taxa.percentil * 100) : '50'} onChange={(e) => { setV({ ...v, percentil: e.target.value }); }} />
            </Campo>
            <Campo id={`${base}-h`} rotulo="Horizonte (h de uso)" ajuda="Recomendado: 250 h.">
              <input id={`${base}-h`} inputMode="decimal" className="controle tabular-nums" value={v.horizonteH} placeholder={sugeridos ? String(sugeridos.taxa.horizonteH) : '250'} onChange={(e) => { setV({ ...v, horizonteH: e.target.value }); }} />
            </Campo>
          </div>
        ) : (
          <>
            <CampoSelecao
              rotulo="Limite do fabricante"
              valor={v.limiarId}
              aoMudar={(x) => { setV({ ...v, limiarId: x }); }}
              opcoes={limiares.map((l) => ({ valor: l.id, rotulo: `${ROTULO_DO_SINAL[l.tipo]} ${l.sentido} de ${formatarDecimal(l.limite, 1).replace(/,0$/, '')} ${UNIDADE_DO_SINAL[l.tipo]} · ${l.documento}, p. ${formatarInteiro(l.pagina)}` }))}
              rotuloVazio={v.modeloId && limiares.length === 0 ? 'Nenhum limite deste modelo no catálogo' : 'Escolha o limite'}
            />
            <div className="grid grid-cols-3 gap-3">
              <Campo id={`${base}-j`} rotulo="Janela (h)">
                <input id={`${base}-j`} inputMode="numeric" className="controle tabular-nums" value={v.janelaH} placeholder={sugeridos ? String(sugeridos.limiar.janelaH) : '24'} onChange={(e) => { setV({ ...v, janelaH: e.target.value }); }} />
              </Campo>
              <Campo id={`${base}-l`} rotulo="Leituras além">
                <input id={`${base}-l`} inputMode="numeric" className="controle tabular-nums" value={v.leiturasMinimas} placeholder={sugeridos ? String(sugeridos.limiar.leiturasMinimas) : '3'} onChange={(e) => { setV({ ...v, leiturasMinimas: e.target.value }); }} />
              </Campo>
              <Campo id={`${base}-d`} rotulo="Horizonte (dias)">
                <input id={`${base}-d`} inputMode="numeric" className="controle tabular-nums" value={v.horizonteDias} placeholder={sugeridos ? String(sugeridos.limiar.horizonteDias) : '14'} onChange={(e) => { setV({ ...v, horizonteDias: e.target.value }); }} />
              </Campo>
            </div>
          </>
        )}
      </div>
    </Sobreposicao>
  );
}

export function JanelaDeBacktest({ alvo, aoFechar }: { readonly alvo: ModeloPreditivo | null; readonly aoFechar: () => void }) {
  const id = useId();
  const invalidar = useInvalidar();
  const [corte, setCorte] = useState(diaDoCampo(180));
  const [resultado, setResultado] = useState<ResultadoDoBacktest | null>(null);
  const medir = useMutation({
    mutationFn: () => apiDaPreditiva.backtest(alvo?.id ?? '', corte),
    meta: { retorno: retornoDe<ResultadoDoBacktest>((r) => ({ titulo: r.abstencao ? 'Backtest sem medida' : 'Backtest medido', ...retornoDoBacktest(r) })), silenciarFalha: true },
    onSuccess: (r) => {
      invalidar();
      setResultado(r);
    },
  });
  function fechar() {
    setResultado(null);
    medir.reset();
    aoFechar();
  }
  return (
    <Janela
      titulo={alvo ? `Backtest de ${alvo.nome}` : 'Backtest'}
      descricao="Treina só com o que veio antes do corte e mede o que veio depois: as confirmadas, os falsos alertas e as falhas não previstas."
      aberta={alvo !== null}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>
            Fechar
          </Botao>
          <Botao nivel="primario" disabled={!/^\d{4}-\d{2}-\d{2}$/.test(corte)} carregando={medir.isPending} onClick={() => { medir.mutate(); }}>
            Rodar backtest
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {medir.isError ? <Recusa erro={medir.error} /> : null}
        <Campo id={`${id}-corte`} rotulo="Data de corte" ajuda="O treino é o que veio antes dela; o teste, o que veio depois.">
          <input id={`${id}-corte`} type="date" className="controle" value={corte} max={diaDoCampo(1)} onChange={(e) => { setCorte(e.target.value); }} />
        </Campo>
        {resultado ? <p className="txt-corpo text-pretty text-tinta">{backtestEscrito(resultado)}</p> : null}
      </div>
    </Janela>
  );
}

export function JanelaDeRetirada({ alvo, aoFechar }: { readonly alvo: ModeloPreditivo | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [motivo, setMotivo] = useState('');
  const retirar = useMutation({
    mutationFn: () => apiDaPreditiva.retirar(alvo?.id ?? '', motivo.trim()),
    meta: {
      retorno: retornoDe<{ nome: string }>((r) => ({ titulo: 'Modelo retirado', alvo: { nome: r.nome }, efeito: 'Não roda mais; as previsões e o placar dele ficam como histórico' })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      setMotivo('');
      aoFechar();
    },
  });
  return (
    <Janela
      titulo={alvo ? `Retirar o modelo ${alvo.nome}?` : 'Retirar o modelo?'}
      descricao="Ele não roda mais e não volta. As previsões que emitiu e o placar dele ficam como histórico."
      aberta={alvo !== null}
      largura="p"
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Manter modelo
          </Botao>
          <Botao nivel="destrutivo" disabled={motivo.trim().length < 10} carregando={retirar.isPending} onClick={() => { retirar.mutate(); }}>
            Retirar modelo
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {retirar.isError ? <Recusa erro={retirar.error} /> : null}
        <CampoTexto rotulo="Motivo" valor={motivo} aoMudar={setMotivo} placeholder="Muitos falsos alertas no piloto" ajuda="Pelo menos 10 caracteres." maxLength={400} />
      </div>
    </Janela>
  );
}

/* ================================================== o desfecho da previsão */

export function JanelaDeDesfecho({ alvo, aoFechar }: { readonly alvo: Previsao | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const ordens = useOrdensDaPrevisao(alvo?.id ?? null);
  const [situacao, setSituacao] = useState<'confirmada' | 'falso_alerta'>('falso_alerta');
  const [ordemId, setOrdemId] = useState('');
  const [nota, setNota] = useState('');
  const registrar = useMutation({
    mutationFn: () => apiDaPreditiva.desfecho(alvo?.id ?? '', { situacao, ordemId: situacao === 'confirmada' ? ordemId : null, nota: nota.trim() || null }),
    meta: {
      retorno: retornoDe<{ situacao: string; equipamento: string; componente: string; numero: number | null; antecedencia: number | null; unidade: 'h' | 'dias' }>((r) => ({
        titulo: 'Desfecho registrado',
        ...retornoDoDesfecho(r),
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      setNota('');
      setOrdemId('');
      aoFechar();
    },
  });
  const pronto = situacao === 'confirmada' ? ordemId !== '' : nota.trim().length >= 10;
  return (
    <Janela
      titulo={alvo ? `Desfecho da previsão de ${alvo.componente} na ${alvo.equipamento}` : 'Desfecho da previsão'}
      descricao={alvo ? `${valorContraLimite(alvo)}. O desfecho conta no placar do modelo e não muda depois.` : ''}
      aberta={alvo !== null}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" disabled={!pronto} carregando={registrar.isPending} onClick={() => { registrar.mutate(); }}>
            Registrar desfecho
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {registrar.isError ? <Recusa erro={registrar.error} /> : null}
        <Seletor rotulo="Desfecho" valor={situacao} aoEscolher={setSituacao} opcoes={[{ valor: 'falso_alerta', rotulo: 'Falso alerta' }, { valor: 'confirmada', rotulo: 'A falha veio' }]} />
        {situacao === 'confirmada' ? (
          (ordens.data?.ordens.length ?? 0) === 0 ? (
            <p className="txt-apoio text-pretty text-tinta-secundaria">
              Nenhuma corretiva desta máquina, aberta depois da emissão, está classificada com o componente. Classifique a O.S. da falha no histórico e volte aqui.
            </p>
          ) : (
            <CampoSelecao
              rotulo="A O.S. da falha"
              valor={ordemId}
              aoMudar={setOrdemId}
              opcoes={(ordens.data?.ordens ?? []).map((o) => ({ valor: o.id, rotulo: `${numeroDaOrdem(o.numero)} · ${quando(o.falhouEm)}` }))}
              rotuloVazio="Escolha a O.S."
            />
          )
        ) : null}
        <CampoTexto
          rotulo={situacao === 'confirmada' ? 'Nota' : 'O que a inspeção achou'}
          opcional={situacao === 'confirmada'}
          valor={nota}
          aoMudar={setNota}
          placeholder="Radiador limpo, sem vazamento"
          {...(situacao === 'falso_alerta' ? { ajuda: 'Pelo menos 10 caracteres.' } : {})}
          maxLength={1000}
        />
      </div>
    </Janela>
  );
}

/* ================================================== a proposta técnica */

export interface InicialDaProposta {
  readonly tipo?: 'inspecao' | 'ajuste_de_plano';
  readonly equipamentoId?: string;
  readonly equipamento?: string;
  readonly componenteId?: string;
  readonly investigacaoId?: string;
  readonly previsaoId?: string;
}

export function GavetaDeProposta({ aberta, inicial, aoFechar }: { readonly aberta: boolean; readonly inicial: InicialDaProposta; readonly aoFechar: () => void }) {
  const base = useId();
  const invalidar = useInvalidar();
  const opcoes = useOpcoesDaProposta(aberta);
  const o = opcoes.data;
  const [tipo, setTipo] = useState<'inspecao' | 'ajuste_de_plano'>(inicial.tipo ?? 'inspecao');
  const [termo, setTermo] = useState('');
  const maquinas = useMaquinasDaPreditiva(termo, aberta && tipo === 'inspecao' && !inicial.equipamentoId);
  const comVisualDaFrota = useOpcoesComVisualDaFrota(aberta && tipo === 'inspecao' && !inicial.equipamentoId);
  const [v, setV] = useState({
    equipamentoId: inicial.equipamentoId ?? '',
    planoId: '',
    tarefaId: '',
    componenteId: inicial.componenteId ?? '',
    investigacaoId: inicial.investigacaoId ?? '',
    intervalo: '',
    documentoId: '',
    pagina: '',
    contraria: false,
    justificativa: '',
  });
  const plano = o?.planos.find((p) => p.id === v.planoId) ?? null;
  const tarefa = plano?.tarefas.find((t) => t.id === v.tarefaId) ?? null;
  const intervalo = interpretarNumero(v.intervalo);
  const pagina = interpretarNumero(v.pagina);
  /* Alargar o intervalo de um plano com o manual é divergir do fabricante: a
     tela já mostra, e o banco marca do mesmo jeito. */
  const alarga = tipo === 'ajuste_de_plano' && tarefa !== null && intervalo !== null && intervalo > tarefa.intervalo && (plano?.documentoId !== null || tarefa.pagina !== null);
  const contraria = v.contraria || alarga;
  const propor = useMutation({
    mutationFn: () =>
      apiDaPreditiva.propor({
        tipo,
        equipamentoId: tipo === 'inspecao' ? v.equipamentoId || null : null,
        planoId: tipo === 'ajuste_de_plano' ? v.planoId || null : null,
        tarefaId: tipo === 'ajuste_de_plano' ? v.tarefaId || null : null,
        componenteId: v.componenteId || null,
        investigacaoId: v.investigacaoId || null,
        previsaoId: inicial.previsaoId ?? null,
        intervaloProposto: tipo === 'ajuste_de_plano' ? intervalo : null,
        documentoId: v.documentoId || (alarga ? plano?.documentoId ?? null : null),
        pagina: pagina ?? (alarga ? tarefa?.pagina ?? null : null),
        contrariaFabricante: v.contraria,
        justificativa: v.justificativa.trim(),
      }),
    meta: { retorno: retornoDe<{ tipo: string; contrariaFabricante: boolean; equipamento: string | null }>((r) => ({ titulo: 'Proposta técnica enviada', ...retornoDaProposta(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      aoFechar();
    },
  });
  const minimo = contraria ? 30 : 15;
  const pronto =
    v.justificativa.trim().length >= minimo &&
    (tipo === 'inspecao' ? v.equipamentoId !== '' : v.planoId !== '' && v.tarefaId !== '' && intervalo !== null && intervalo > 0) &&
    (!v.contraria || (v.documentoId !== '' && pagina !== null));
  return (
    <Sobreposicao
      lugar="direita"
      titulo="Nova proposta técnica"
      descricao="Vai para a decisão técnica: quem propõe não decide, e nada muda sozinho. Aceita, a inspeção vira O.S.; o ajuste abre a revisão do plano em rascunho."
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" disabled={!pronto} carregando={propor.isPending} onClick={() => { propor.mutate(); }}>
            Enviar proposta
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {propor.isError ? <Recusa erro={propor.error} /> : null}
        <Seletor rotulo="Tipo da proposta" valor={tipo} aoEscolher={setTipo} opcoes={[{ valor: 'inspecao', rotulo: 'Inspeção' }, { valor: 'ajuste_de_plano', rotulo: 'Ajuste de plano' }]} />
        {tipo === 'inspecao' ? (
          inicial.equipamentoId ? (
            <p className="txt-corpo text-tinta">{`Máquina: ${inicial.equipamento ?? ''}`}</p>
          ) : (
            <>
              <CampoTexto rotulo="Buscar a máquina" valor={termo} aoMudar={setTermo} placeholder="ESC-001" />
              <CampoSelecao rotulo="Máquina" valor={v.equipamentoId} aoMudar={(x) => { setV({ ...v, equipamentoId: x }); }} opcoes={comVisualDaFrota((maquinas.data?.maquinas ?? []).map((m) => opcaoDaMaquina(m)))} rotuloVazio="Escolha a máquina" adesivo />
            </>
          )
        ) : (
          <>
            <CampoSelecao rotulo="Plano publicado" valor={v.planoId} aoMudar={(x) => { setV({ ...v, planoId: x, tarefaId: '' }); }} opcoes={(o?.planos ?? []).map((p) => ({ valor: p.id, rotulo: p.nome }))} rotuloVazio="Escolha o plano" />
            <CampoSelecao
              rotulo="Tarefa"
              valor={v.tarefaId}
              aoMudar={(x) => { setV({ ...v, tarefaId: x }); }}
              opcoes={(plano?.tarefas ?? []).map((t) => ({ valor: t.id, rotulo: `${t.descricao} · a cada ${formatarDecimal(t.intervalo, 1).replace(/,0$/, '')} ${t.gatilho === 'horas' ? 'h' : t.gatilho}` }))}
              rotuloVazio="Escolha a tarefa"
            />
            <Campo
              id={`${base}-int`}
              rotulo={`Intervalo proposto${tarefa ? ` (${tarefa.gatilho === 'horas' ? 'h' : tarefa.gatilho})` : ''}`}
              {...(tarefa ? { ajuda: `O atual é ${formatarDecimal(tarefa.intervalo, 1).replace(/,0$/, '')}. A proposta não muda o plano: quem publica a revisão decide.` } : {})}
            >
              <input id={`${base}-int`} inputMode="decimal" className="controle tabular-nums" value={v.intervalo} placeholder="250" onChange={(e) => { setV({ ...v, intervalo: e.target.value }); }} />
            </Campo>
          </>
        )}
        <CampoSelecao rotulo="Componente" opcional valor={v.componenteId} aoMudar={(x) => { setV({ ...v, componenteId: x }); }} opcoes={(o?.componentes ?? []).map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.nome}` }))} rotuloVazio="Sem componente" />
        <CampoSelecao rotulo="Investigação de origem" opcional valor={v.investigacaoId} aoMudar={(x) => { setV({ ...v, investigacaoId: x }); }} opcoes={(o?.investigacoes ?? []).map((i) => ({ valor: i.id, rotulo: i.titulo }))} rotuloVazio="Nenhuma" />
        {alarga ? (
          <div role="note" className="flex flex-col gap-1 rounded-controle bg-atencao-fundo p-3">
            <span className="txt-corpo text-atencao-texto">O intervalo proposto alarga o do plano, que segue o manual do fabricante: a proposta contraria o fabricante.</span>
            <span className="txt-apoio text-tinta">{`O manual e a página do plano vão junto${plano?.documento ? ` (${plano.documento}${tarefa?.pagina ? `, p. ${formatarInteiro(tarefa.pagina)}` : ''})` : ''}. Justifique com pelo menos 30 caracteres.`}</span>
          </div>
        ) : (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={v.contraria} onChange={(e) => { setV({ ...v, contraria: e.target.checked }); }} />
            <span className="txt-corpo text-tinta">A proposta contraria o fabricante</span>
          </label>
        )}
        {v.contraria ? (
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-3">
            <CampoSelecao rotulo="Manual do fabricante" valor={v.documentoId} aoMudar={(x) => { setV({ ...v, documentoId: x }); }} opcoes={(o?.documentos ?? []).map((d) => ({ valor: d.id, rotulo: d.titulo }))} rotuloVazio="Escolha o manual" />
            <Campo id={`${base}-pag`} rotulo="Página">
              <input id={`${base}-pag`} inputMode="numeric" className="controle tabular-nums" value={v.pagina} placeholder="112" onChange={(e) => { setV({ ...v, pagina: e.target.value }); }} />
            </Campo>
          </div>
        ) : null}
        <CampoTexto
          rotulo="Justificativa"
          valor={v.justificativa}
          aoMudar={(x) => { setV({ ...v, justificativa: x }); }}
          placeholder="Nove falhas do radiador mostram intervalo médio de 620 h na obra seca"
          ajuda={`Pelo menos ${String(minimo)} caracteres. Cite os fatos: as O.S., a recorrência, a previsão.`}
          maxLength={4000}
        />
      </div>
    </Sobreposicao>
  );
}

export function JanelaDeDecisao({ alvo, eu, aoFechar }: { readonly alvo: PropostaTecnica | null; readonly eu: string; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [aceitar, setAceitar] = useState<'sim' | 'nao'>('sim');
  const [nota, setNota] = useState('');
  const decidir = useMutation({
    mutationFn: () => apiDaPreditiva.decidir(alvo?.id ?? '', aceitar === 'sim', nota.trim()),
    meta: {
      retorno: retornoDe<{ situacao: string; tipo: string; ordem: { rotulo: string } | null; revisaoPlanoId: string | null }>((r) => ({
        titulo: r.situacao === 'aceita' ? 'Proposta aceita' : 'Proposta recusada',
        ...retornoDaDecisao(r),
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      setNota('');
      aoFechar();
    },
  });
  const mesmaPessoa = alvo !== null && alvo.propostaPorId === eu;
  return (
    <Janela
      titulo={alvo ? `Decidir a proposta de ${alvo.tipo === 'inspecao' ? 'inspeção' : 'ajuste de plano'}` : 'Decidir a proposta'}
      descricao={
        alvo?.tipo === 'ajuste_de_plano'
          ? 'Aceitar abre a revisão do plano em rascunho. O intervalo só muda quando alguém editar a revisão e publicar com a nota.'
          : 'Aceitar abre a O.S. de inspeção da máquina, pela abertura de sempre.'
      }
      aberta={alvo !== null}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" disabled={mesmaPessoa || nota.trim().length < 10} carregando={decidir.isPending} onClick={() => { decidir.mutate(); }}>
            {aceitar === 'sim' ? 'Aceitar proposta' : 'Recusar proposta'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {decidir.isError ? <Recusa erro={decidir.error} /> : null}
        {mesmaPessoa ? <p className="txt-corpo text-atencao-texto">Você propôs: quem propõe não decide. Outra pessoa com a decisão técnica decide esta proposta.</p> : null}
        {alvo?.contrariaFabricante ? (
          <div role="note" className="flex flex-col gap-1 rounded-controle bg-atencao-fundo p-3">
            <SeloDaPreditiva rotulo="Contraria o fabricante" tom="atencao" />
            <span className="txt-apoio text-tinta">{`${alvo.documento ?? 'Manual'}${alvo.pagina ? `, p. ${formatarInteiro(alvo.pagina)}` : ''}. Confira a justificativa: "${alvo.justificativa}"`}</span>
          </div>
        ) : null}
        <Seletor rotulo="Decisão" valor={aceitar} aoEscolher={setAceitar} opcoes={[{ valor: 'sim', rotulo: 'Aceitar' }, { valor: 'nao', rotulo: 'Recusar' }]} />
        <CampoTexto rotulo="Nota da decisão" valor={nota} aoMudar={setNota} placeholder="Aceito para revisão técnica do plano" ajuda="Pelo menos 10 caracteres. Fica com a proposta e na trilha." maxLength={2000} />
      </div>
    </Janela>
  );
}
